import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PackageCard from '../components/PackageCard';
import { Package } from '../types';
import { destinations } from '../utils/packageData';
import { getMinimumListingPrice } from '../utils/pricing';
import '../styling/Home.css';

// Get popular packages from destinations
const popularPackages: Package[] = destinations
  .flatMap(dest => dest.packages)
  .slice(0, 3); // Show first 3 packages

interface QuoteFormData {
  destination: string;
  package: string;
  startDate: string;
  adults: string;
  children: string;
  toddler: string;
  duration: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [quoteFormData, setQuoteFormData] = useState<QuoteFormData>({
    destination: '',
    package: '',
    startDate: '',
    adults: '',
    children: '',
    toddler: '',
    duration: ''
  });

  const [availablePackages, setAvailablePackages] = useState<Package[]>([]);

  // Update available packages when destination changes
  useEffect(() => {
    if (quoteFormData.destination) {
      const selectedDestination = destinations.find(dest => dest.id === quoteFormData.destination);
      if (selectedDestination) {
        setAvailablePackages(selectedDestination.packages);
        // Reset package selection when destination changes
        setQuoteFormData(prev => ({ ...prev, package: '' }));
      }
    } else {
      setAvailablePackages([]);
      setQuoteFormData(prev => ({ ...prev, package: '' }));
    }
  }, [quoteFormData.destination]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    setQuoteFormData({
      ...quoteFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuoteSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    
    const selectedDestination = destinations.find(dest => dest.id === quoteFormData.destination);
    const selectedPackage = availablePackages.find(pkg => pkg.id.toString() === quoteFormData.package);
    
    const message = `Hey Travelisk! I'm interested in getting a quote for a travel package.

*Travel Details:*
Destination: ${selectedDestination?.name || quoteFormData.destination}
Package: ${selectedPackage?.title || quoteFormData.package}
Start Date: ${quoteFormData.startDate}
Duration: ${quoteFormData.duration}

*Group Size:*
Adults (Above 18 years): ${quoteFormData.adults}
Children (Above 5 years): ${quoteFormData.children}
Toddler (Below 5 years): ${quoteFormData.toddler}

Please provide me with a customized quote for this trip. Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    
    window.open(`https://wa.me/7980013170?text=${encodedMessage}`, '_blank');
    
    setQuoteFormData({
      destination: '',
      package: '',
      startDate: '',
      adults: '',
      children: '',
      toddler: '',
      duration: ''
    });
  };

  return (
    <div>
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Discover Your Next Adventure</h1>
          <p className="hero-subtitle">
            Explore amazing destinations and create unforgettable memories with our curated travel packages
          </p>
        </div>
      </section>

      <div className="container">
        <div className="search-container">
          <form className="search-form" onSubmit={handleQuoteSubmit}>
            <h2 className="form-title">Get a Custom Quote</h2>
            <p className="form-subtitle">Fill in the details below and we'll get back to you with a personalized quote!</p>
            
            <div className="form-group">
              <label className="form-label">Destination</label>
              <select 
                className="form-select" 
                name="destination" 
                value={quoteFormData.destination} 
                onChange={handleInputChange}
                required
              >
                <option value="">Select Destination</option>
                {destinations.map(dest => (
                  <option key={dest.id} value={dest.id}>
                    {dest.name}, {dest.country}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Package</label>
              <select 
                className="form-select" 
                name="package" 
                value={quoteFormData.package} 
                onChange={handleInputChange}
                required
                disabled={!quoteFormData.destination}
              >
                <option value="">
                  {quoteFormData.destination ? 'Select Package' : 'Select Destination First'}
                </option>
                {availablePackages.map(pkg => {
                  const { price } = getMinimumListingPrice(pkg.pricing);

                  return (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.title} - {pkg.overview.duration} (₹{price.toLocaleString()})
                    </option>
                  );
                })}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input 
                type="date" 
                className="form-input" 
                name="startDate" 
                value={quoteFormData.startDate} 
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Adults (Above 18 years)</label>
                <select 
                  className="form-select" 
                  name="adults" 
                  value={quoteFormData.adults} 
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6+</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Children (Above 5 years)</label>
                <select 
                  className="form-select" 
                  name="children" 
                  value={quoteFormData.children} 
                  onChange={handleInputChange}
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5+</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Toddler (Below 5 years)</label>
                <select 
                  className="form-select" 
                  name="toddler" 
                  value={quoteFormData.toddler} 
                  onChange={handleInputChange}
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4+</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Duration</label>
                <select 
                  className="form-select" 
                  name="duration" 
                  value={quoteFormData.duration} 
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Duration</option>
                  <option value="2 Night - 3 Days">2 Night - 3 Days</option>
                  <option value="3 Night - 4 Days">3 Night - 4 Days</option>
                  <option value="4 Night - 5 Days">4 Night - 5 Days</option>
                  <option value="5 Night - 6 Days">5 Night - 6 Days</option>
                  <option value="6 Night - 7 Days">6 Night - 7 Days</option>
                  <option value="7 Night - 8 Days">7 Night - 8 Days</option>
                  <option value="8 Night - 9 Days">8 Night - 9 Days</option>
                  <option value="9 Night - 10 Days">9 Night - 10 Days</option>
                  <option value="10 Night - 11 Days">10 Night - 11 Days</option>
                  <option value="11+ Days">11+ Days</option>
                </select>
              </div>
            </div>
            
            <button type="submit" className="search-button">
              Get Quote on WhatsApp
            </button>
          </form>
        </div>

        <section>
          <h2 className="section-title">Popular Packages</h2>
          <div className="package-grid">
            {popularPackages.map(pkg => (
              <PackageCard key={pkg.id} pkg={pkg} />
            ))}
          </div>
          <button 
            className="see-all-button" 
            onClick={() => navigate('/packages')}
          >
            See All Packages
          </button>
        </section>
      </div>
    </div>
  );
};

export default Home; 